import { getConfigurationValue } from "@/modules/targets/api";
import {
  CommonSalesDetailsSummary,
  CommonTargetInfo,
  ConfigFactors,
  MonthInfo,
  QuarterInfo,
  SaleTypeName,
} from "@/modules/targets/types";
import { muiTheme } from "@/styles/theme";
import { endOfDay } from "date-fns";
import { nawyTheme } from "../../../styles/nawy-theme/nawy-theme";

export const calculateTargets = (
  selectedTarget: string,
  info: CommonTargetInfo
) => {
  const targetMap = {
    Total: { target: info.target, achieved: info.total_sales },
    Resale: { target: info.resale_target, achieved: info.resale_achieved },
    "Nawy Now": {
      target: info.finance_target,
      achieved: info.finance_achieved,
    },
    Primary: { target: info.primary_target, achieved: info.primary_achieved },
    Commercial: {
      target: info.commercial_target,
      achieved: info.commercial_achieved,
    },
    "Prim+Comm": {
      target: info.primary_target + info.commercial_target,
      achieved: info.primary_achieved + info.commercial_achieved,
    },
    "Resale+NN": {
      target: info.finance_target + info.resale_target,
      achieved: info.finance_achieved + info.resale_achieved,
    },
  };
  // Get value, If the selected target is not found in the targetMap, we default to 0
  const { target, achieved } = targetMap[
    selectedTarget as keyof typeof targetMap
  ] || {
    target: 0,
    achieved: 0,
  };
  // Calculate the percentage based on the retrieved target and achieved values
  const tempPercentage = target !== 0 ? achieved / target : 0;
  return { tempTarget: target, tempAchieved: achieved, tempPercentage };
};

export const loadConfigs = async (): Promise<ConfigFactors> => {
  let blockedFactor = 0;
  let reservedFactor = 0;
  let eoiFactor = 0;
  try {
    const blockedFactorResponse = await getConfigurationValue(
      "blocked_target_factor"
    );
    blockedFactor = parseFloat(blockedFactorResponse.data.conf_value) / 100;
  } catch (err) {
    blockedFactor = 0.5;
    console.error("Error parsing blocked factor", err);
  }
  try {
    const reservedFactorResponse = await getConfigurationValue(
      "reserved_target_factor"
    );
    reservedFactor = parseFloat(reservedFactorResponse.data.conf_value) / 100;
  } catch (err) {
    reservedFactor = 0.9;
    console.error("Error parsing reserved factor", err);
  }
  try {
    const eoiFactorResponse = await getConfigurationValue("eoi_target_factor");
    eoiFactor = parseFloat(eoiFactorResponse.data.conf_value) / 100;
  } catch (err) {
    eoiFactor = 0.5;
    console.error("Error parsing eoi factor", err);
  }
  return { eoiFactor, reservedFactor, blockedFactor };
};

export const getQuarterlyAchievedData = (
  quarterlyAchieved?: CommonSalesDetailsSummary
): {
  label: {
    mobile: string;
    desktop: string;
  };
  value?: string | number;
  backgroundColor: string;
  newHomePageTargetBackgroundColor: string;
  hoverColor?: string;
  activeColor?: string;
  detailsTab?: SaleTypeName;
  clickable?: boolean;
}[] => {
  return [
    {
      label: {
        mobile: "W.F. Actions",
        desktop: "Weighted First Actions",
      },
      value: Math.floor(quarterlyAchieved?.weightedFirstAction ?? 0).toLocaleString(),
      backgroundColor: "#D9DCDD",
      newHomePageTargetBackgroundColor:
        nawyTheme.nawy.colors.semantic.indicator.blueLight ?? "#CBE3F4",
      clickable: false,
    },
    {
      label: {
        mobile: "EOI",
        desktop: "EOI",
      },
      value: Math.floor(quarterlyAchieved?.totalEoi ?? 0).toLocaleString(),
      backgroundColor: "#E3E7C2",
      newHomePageTargetBackgroundColor: "#FF9502",
      hoverColor: "#D8DCA7",
      activeColor: "#CDD18C",
      detailsTab: "EOI",
      clickable: true,
    },
    {
      label: {
        mobile: "Reserved",
        desktop: "Reserved",
      },
      value: Math.floor(quarterlyAchieved?.totalReserved ?? 0).toLocaleString(),
      backgroundColor: "#E9C2C5",
      newHomePageTargetBackgroundColor: "#A8A823",
      hoverColor: "#DFA7AB",
      activeColor: "#D58C91",
      detailsTab: "Reserved",
      clickable: true,
    },
    {
      label: {
        mobile: "Unit Blocked",
        desktop: "Unit Blocked",
      },
      value: Math.floor(quarterlyAchieved?.totalBlocked ?? 0).toLocaleString(),
      backgroundColor: muiTheme.colorSchemes.light.palette.main.actionDisabled,
      newHomePageTargetBackgroundColor: "#FF9502",
      hoverColor: "#D1E5ED",
      activeColor: "#BDDAE5",
      detailsTab: "Blocked",
      clickable: true,
    },
  ];
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const monthShortNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const getQuarterStartDate = (year: number, quarter: number): Date => {
  const startMonth = (quarter - 1) * 3;
  return new Date(year, startMonth, 1);
};

export const getQuarterInfo = (date: Date): QuarterInfo => {
  const year = date.getFullYear();
  const month = date.getMonth();

  const quarterNumber = (Math.floor(month / 3) + 1) as 1 | 2 | 3 | 4;
  const startMonth = (quarterNumber - 1) * 3;
  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, startMonth + 3, 0, 23, 59, 59);

  const startMonthName = monthNames[startMonth];
  const endMonthName = monthNames[startMonth + 2];
  const monthRange = `${startMonthName} - ${endMonthName}`;

  return {
    quarterNumber,
    startDate,
    endDate,
    year,
    quarterLabel: `Q${quarterNumber} ${year}`,
    monthRange,
    m1: monthNames[startMonth],
    m2: monthNames[startMonth + 1],
    m3: monthNames[startMonth + 2],
    m1StartDate: new Date(year, startMonth, 1, 0, 0, 0, 0),
    m2StartDate: new Date(year, startMonth + 1, 1, 0, 0, 0, 0),
    m3StartDate: new Date(year, startMonth + 2, 1, 0, 0, 0, 0),
    m1EndDate: new Date(year, startMonth + 1, 0, 23, 59, 59),
    m2EndDate: new Date(year, startMonth + 2, 0, 23, 59, 59),
    m3EndDate: new Date(year, startMonth + 3, 0, 23, 59, 59),
  };
};

export const getCurrentQuarter = (): QuarterInfo => {
  const today = new Date();
  return getQuarterInfo(today);
};

export const getCurrentMonth = (): MonthInfo => {
  const today = new Date();
  const year = today.getFullYear();
  const monthNumber = today.getMonth() + 1;

  const startDate = new Date(year, monthNumber - 1, 1);
  const endDate = endOfDay(new Date(year, monthNumber, 0));

  return {
    monthNumber,
    startDate,
    endDate,
    year,
    monthLabel: `${monthNames[monthNumber - 1]} ${year}`,
    monthShort: monthShortNames[monthNumber - 1],
    monthLong: monthNames[monthNumber - 1],
    daysInMonth: endDate.getDate(),
  };
};

export const formatDateToString = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

export const getCurrentQuarterMonths = (): {
  month1: { from: string; to: string };
  month2: { from: string; to: string };
  month3: { from: string; to: string };
} => {
  const currentQuarter = getCurrentQuarter();

  const formatRange = (from: Date, to: Date) => ({
    from: new Date(from).toISOString(),
    to: new Date(to.setHours(20, 59, 59, 0)).toISOString(),
  });

  return {
    month1: formatRange(currentQuarter.m1StartDate, currentQuarter.m1EndDate),
    month2: formatRange(currentQuarter.m2StartDate, currentQuarter.m2EndDate),
    month3: formatRange(currentQuarter.m3StartDate, currentQuarter.m3EndDate),
  };
};

export const generateTargetPeriods = (
  currentQuarter: {
    month1: { from: string; to: string };
    month2: { from: string; to: string };
    month3: { from: string; to: string };
  },
  personalTarget: { m1: number; m2: number; m3: number },
  minimumTarget: { m1: number; m2: number; m3: number }
) => {
  const monthTargets = [
    {
      month: currentQuarter.month1,
      personal: personalTarget.m1,
      minimum: minimumTarget.m1,
    },
    {
      month: currentQuarter.month2,
      personal: personalTarget.m2,
      minimum: minimumTarget.m2,
    },
    {
      month: currentQuarter.month3,
      personal: personalTarget.m3,
      minimum: minimumTarget.m3,
    },
  ];

  return monthTargets.map(({ month, personal, minimum }) => ({
    from: new Date(month.from),
    to: new Date(month.to),
    amount: personal,
    minimumTarget: minimum,
  }));
};
